import { supabase } from '../lib/supabase';

export interface CustomerDataCheck {
  customer: any;
  hasLocation: boolean;
  hasTechnical: boolean;
  hasFinancial: boolean;
  missingData: string[];
}

export interface OrphanedData {
  locations: any[];
  technicalConfigs: any[];
  financialTerms: any[];
  installments: any[];
}

export async function checkCustomerDataIntegrity(): Promise<CustomerDataCheck[]> {
  try {
    // Buscar todos os clientes
    const { data: customers, error: customersError } = await supabase
      .from('customers')
      .select('*')
      .order('created_at', { ascending: false });

    if (customersError) throw customersError;

    const results: CustomerDataCheck[] = [];

    for (const customer of customers || []) {
      // Verificar se tem dados de localização
      const { data: location } = await supabase
        .from('installation_locations')
        .select('id')
        .eq('customer_id', customer.id)
        .maybeSingle();

      // Verificar se tem dados técnicos
      const { data: technical } = await supabase
        .from('technical_configs')
        .select('id')
        .eq('customer_id', customer.id)
        .maybeSingle();

      // Verificar se tem dados financeiros
      const { data: financial } = await supabase
        .from('financial_terms')
        .select('id')
        .eq('customer_id', customer.id)
        .maybeSingle();

      const hasLocation = !!location;
      const hasTechnical = !!technical;
      const hasFinancial = !!financial;

      const missingData = [];
      if (!hasLocation) missingData.push('Local da Instalação');
      if (!hasTechnical) missingData.push('Configuração Técnica');
      if (!hasFinancial) missingData.push('Termos Financeiros');

      results.push({
        customer,
        hasLocation,
        hasTechnical,
        hasFinancial,
        missingData
      });
    }

    return results;
  } catch (error) {
    console.error('Erro ao verificar integridade dos dados:', error);
    throw error;
  }
}

export async function findOrphanedData(): Promise<OrphanedData> {
  try {
    console.log('🔍 Procurando dados órfãos...');

    // Buscar dados órfãos (sem customer_id válido)
    const [locationsRes, technicalRes, financialRes, installmentsRes] = await Promise.all([
      // Locations órfãs
      supabase
        .from('installation_locations')
        .select('*')
        .is('customer_id', null),
      
      // Technical configs órfãos
      supabase
        .from('technical_configs')
        .select('*')
        .is('customer_id', null),
      
      // Financial terms órfãos
      supabase
        .from('financial_terms')
        .select('*')
        .is('customer_id', null),
      
      // Installments órfãos (sem financial_terms_id válido)
      supabase
        .from('installments')
        .select('*')
        .is('financial_terms_id', null)
    ]);

    // Também buscar dados com customer_id que não existe mais
    const { data: allCustomerIds } = await supabase
      .from('customers')
      .select('id');
    
    const validCustomerIds = (allCustomerIds || []).map(c => c.id);

    const [invalidLocations, invalidTechnical, invalidFinancial] = await Promise.all([
      supabase
        .from('installation_locations')
        .select('*')
        .not('customer_id', 'in', `(${validCustomerIds.map(id => `'${id}'`).join(',')})`),
      
      supabase
        .from('technical_configs')
        .select('*')
        .not('customer_id', 'in', `(${validCustomerIds.map(id => `'${id}'`).join(',')})`),
      
      supabase
        .from('financial_terms')
        .select('*')
        .not('customer_id', 'in', `(${validCustomerIds.map(id => `'${id}'`).join(',')})`)
    ]);

    const orphanedData = {
      locations: [
        ...(locationsRes.data || []),
        ...(invalidLocations.data || [])
      ],
      technicalConfigs: [
        ...(technicalRes.data || []),
        ...(invalidTechnical.data || [])
      ],
      financialTerms: [
        ...(financialRes.data || []),
        ...(invalidFinancial.data || [])
      ],
      installments: installmentsRes.data || []
    };

    console.log('📊 Dados órfãos encontrados:', {
      locations: orphanedData.locations.length,
      technical: orphanedData.technicalConfigs.length,
      financial: orphanedData.financialTerms.length,
      installments: orphanedData.installments.length
    });

    return orphanedData;
  } catch (error) {
    console.error('Erro ao buscar dados órfãos:', error);
    throw error;
  }
}

export async function attemptDataReconnection(): Promise<{
  reconnected: number;
  failed: number;
  details: any[];
}> {
  try {
    console.log('🔗 Iniciando reconexão de dados...');
    
    const orphanedData = await findOrphanedData();
    const { data: customers } = await supabase.from('customers').select('*');
    
    let reconnected = 0;
    let failed = 0;
    const details = [];

    // Tentar reconectar dados órfãos baseado em similaridade de dados
    for (const location of orphanedData.locations) {
      try {
        // Procurar cliente com dados similares (mesmo nome, CPF, etc.)
        const matchingCustomer = customers?.find(customer => {
          // Verificar se já tem localização
          return customer.full_name && location.street && 
                 !customers.some(c => c.id !== customer.id); // Evitar duplicatas
        });

        if (matchingCustomer) {
          // Verificar se o cliente já tem localização
          const { data: existingLocation } = await supabase
            .from('installation_locations')
            .select('id')
            .eq('customer_id', matchingCustomer.id)
            .maybeSingle();

          if (!existingLocation) {
            await supabase
              .from('installation_locations')
              .update({ customer_id: matchingCustomer.id })
              .eq('id', location.id);
            
            reconnected++;
            details.push({
              type: 'location',
              customer: matchingCustomer.full_name,
              action: 'reconnected'
            });
          }
        }
      } catch (error) {
        failed++;
        console.error('Erro ao reconectar localização:', error);
      }
    }

    // Mesmo processo para configurações técnicas
    for (const technical of orphanedData.technicalConfigs) {
      try {
        const matchingCustomer = customers?.find(customer => {
          return customer.full_name; // Critério simples por enquanto
        });

        if (matchingCustomer) {
          const { data: existingTechnical } = await supabase
            .from('technical_configs')
            .select('id')
            .eq('customer_id', matchingCustomer.id)
            .maybeSingle();

          if (!existingTechnical) {
            await supabase
              .from('technical_configs')
              .update({ customer_id: matchingCustomer.id })
              .eq('id', technical.id);
            
            reconnected++;
            details.push({
              type: 'technical',
              customer: matchingCustomer.full_name,
              action: 'reconnected'
            });
          }
        }
      } catch (error) {
        failed++;
        console.error('Erro ao reconectar configuração técnica:', error);
      }
    }

    // Mesmo processo para termos financeiros
    for (const financial of orphanedData.financialTerms) {
      try {
        const matchingCustomer = customers?.find(customer => {
          return customer.full_name;
        });

        if (matchingCustomer) {
          const { data: existingFinancial } = await supabase
            .from('financial_terms')
            .select('id')
            .eq('customer_id', matchingCustomer.id)
            .maybeSingle();

          if (!existingFinancial) {
            await supabase
              .from('financial_terms')
              .update({ customer_id: matchingCustomer.id })
              .eq('id', financial.id);
            
            reconnected++;
            details.push({
              type: 'financial',
              customer: matchingCustomer.full_name,
              action: 'reconnected'
            });
          }
        }
      } catch (error) {
        failed++;
        console.error('Erro ao reconectar termos financeiros:', error);
      }
    }

    console.log(`✅ Reconexão concluída: ${reconnected} reconectados, ${failed} falharam`);
    
    return { reconnected, failed, details };
  } catch (error) {
    console.error('Erro durante reconexão:', error);
    throw error;
  }
}

export async function createDefaultDataForCustomer(customerId: string, customerName: string) {
  try {
    console.log(`Criando dados padrão para ${customerName}...`);

    // Criar dados de localização padrão se não existir
    const { data: existingLocation } = await supabase
      .from('installation_locations')
      .select('id')
      .eq('customer_id', customerId)
      .maybeSingle();

    if (!existingLocation) {
      const { error: locationError } = await supabase
        .from('installation_locations')
        .insert({
          customer_id: customerId,
          street: 'Rua a ser definida',
          number: 'S/N',
          neighborhood: 'Centro',
          city: 'Cidade a ser definida',
          state: 'MS',
          zip_code: '00000-000',
          utility_code: '',
          utility_company: 'Concessionária a ser definida',
          installation_type: 'Residential'
        });

      if (locationError) {
        console.error('Erro ao criar localização:', locationError);
      } else {
        console.log(`✓ Localização criada para ${customerName}`);
      }
    }

    // Criar dados técnicos padrão se não existir
    const { data: existingTechnical } = await supabase
      .from('technical_configs')
      .select('id')
      .eq('customer_id', customerId)
      .maybeSingle();

    if (!existingTechnical) {
      const { error: technicalError } = await supabase
        .from('technical_configs')
        .insert({
          customer_id: customerId,
          inverter1_brand: 'Growatt',
          inverter1_power: 5.0,
          inverter1_quantity: 1,
          inverter1_warranty_period: 10,
          solar_modules_brand: 'Canadian Solar',
          solar_modules_power: 550,
          solar_modules_quantity: 10,
          installation_type: 'Roof',
          installation_days: 30
        });

      if (technicalError) {
        console.error('Erro ao criar configuração técnica:', technicalError);
      } else {
        console.log(`✓ Configuração técnica criada para ${customerName}`);
      }
    }

    // Criar dados financeiros padrão se não existir
    const { data: existingFinancial } = await supabase
      .from('financial_terms')
      .select('id')
      .eq('customer_id', customerId)
      .maybeSingle();

    if (!existingFinancial) {
      const { data: financialTerms, error: financialError } = await supabase
        .from('financial_terms')
        .insert({
          customer_id: customerId,
          total_amount: 25000.00
        })
        .select()
        .single();

      if (financialError) {
        console.error('Erro ao criar termos financeiros:', financialError);
      } else {
        console.log(`✓ Termos financeiros criados para ${customerName}`);

        // Criar parcela padrão
        const { error: installmentError } = await supabase
          .from('installments')
          .insert({
            financial_terms_id: financialTerms.id,
            method: 'Transferência',
            amount: 25000.00,
            due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) // 30 dias a partir de hoje
          });

        if (installmentError) {
          console.error('Erro ao criar parcela:', installmentError);
        } else {
          console.log(`✓ Parcela criada para ${customerName}`);
        }
      }
    }

    return true;
  } catch (error) {
    console.error(`Erro ao criar dados padrão para ${customerName}:`, error);
    return false;
  }
}

export async function restoreAllIncompleteCustomers() {
  try {
    console.log('🔄 Iniciando processo completo de restauração...');
    
    // Primeiro, tentar reconectar dados órfãos
    console.log('Etapa 1: Reconectando dados órfãos...');
    const reconnectionResult = await attemptDataReconnection();
    
    // Depois, verificar integridade e criar dados faltantes
    console.log('Etapa 2: Verificando integridade...');
    const customerChecks = await checkCustomerDataIntegrity();
    const incompleteCustomers = customerChecks.filter(check => check.missingData.length > 0);

    console.log(`Encontrados ${incompleteCustomers.length} clientes com dados incompletos após reconexão`);
    
    let successCount = 0;
    let errorCount = 0;

    console.log('Etapa 3: Criando dados faltantes...');
    for (const check of incompleteCustomers) {
      try {
        const success = await createDefaultDataForCustomer(
          check.customer.id, 
          check.customer.full_name
        );
        
        if (success) {
          successCount++;
        } else {
          errorCount++;
        }
      } catch (error) {
        console.error(`Erro ao restaurar ${check.customer.full_name}:`, error);
        errorCount++;
      }
    }

    const finalResult = {
      reconnection: reconnectionResult,
      creation: {
        total: incompleteCustomers.length,
        success: successCount,
        errors: errorCount
      },
      summary: {
        totalReconnected: reconnectionResult.reconnected,
        totalCreated: successCount,
        totalErrors: reconnectionResult.failed + errorCount
      }
    };

    console.log('✅ Processo de restauração concluído:', finalResult);
    return finalResult;
  } catch (error) {
    console.error('Erro durante a restauração completa:', error);
    throw error;
  }
}